/**
 * Crisp live-chat loader.
 *
 * Injects the Crisp chatbox (client.crisp.chat/l.js) on the public site. The
 * Website ID is not a secret (it ships in the client bundle regardless), so the
 * production ID is baked in as the default and the widget goes live on deploy
 * with no dashboard config. Set the env var to override it: e.g. '' to disable
 * chat locally or in previews.
 *
 * Nothing loads during react-snap pre-rendering (navigator.userAgent ===
 * "ReactSnap"), so the chatbox never bakes into the static SEO HTML or trips
 * the crawl gate. Loading it also requires the client.crisp.chat script origin
 * and the wss://client.relay.crisp.chat websocket in public/_headers CSP.
 */

import { trackEvent } from './analytics';

const CRISP_WEBSITE_ID =
  process.env.REACT_APP_CRISP_WEBSITE_ID ??
  '858a06a3-1f89-4b60-b3d1-7bf9a765cefa';

declare global {
  interface Window {
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
    CRISP_RUNTIME_CONFIG?: { locale?: string };
  }
}

let initialized = false;

const isPrerenderOrServer = (): boolean =>
  typeof window === 'undefined' ||
  typeof document === 'undefined' ||
  navigator.userAgent === 'ReactSnap';

/**
 * Load the Crisp chatbox exactly once. Safe to call repeatedly (subsequent
 * calls no-op) and during pre-rendering (does nothing).
 * @param locale initial widget UI language ("tr" or "en")
 */
export const initCrisp = (locale: 'tr' | 'en'): void => {
  if (initialized || isPrerenderOrServer() || !CRISP_WEBSITE_ID) return;
  initialized = true;

  window.$crisp = [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
  // Set the chatbox UI language up front so first paint matches the page.
  window.CRISP_RUNTIME_CONFIG = { locale };

  // Chat engagement -> analytics: decides whether the widget earns its place
  // (chat_opened volume, and opened -> message conversion).
  window.$crisp.push([
    'on',
    'chat:opened',
    () => trackEvent('chat_opened'),
  ]);
  window.$crisp.push([
    'on',
    'message:sent',
    () => trackEvent('chat_message_sent'),
  ]);

  const script = document.createElement('script');
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.getElementsByTagName('head')[0].appendChild(script);
};
